import type { SupplierPick } from './api';

/** From this many uses in the organization a supplier is one of «I più usati» (board 1.1). */
export const MOST_USED_MIN = 2;

export type PickGroups = {
  /** «Usati da te di recente»: the caller used them, the most recent first. */
  readonly mine: readonly SupplierPick[];
  /** «I più usati in DGB»: the organization's, in the server's order. */
  readonly mostUsed: readonly SupplierPick[];
  /** «Altri»: the rest, in the server's order. */
  readonly others: readonly SupplierPick[];
};

/**
 * The search's answer split into the board's three groups. The server already orders the caller's
 * own first, then the most used, then the closest (contract `searchSuppliers`); the split keeps
 * that order inside each group.
 */
export function groupPicks(picks: readonly SupplierPick[]): PickGroups {
  const mine: SupplierPick[] = [];
  const mostUsed: SupplierPick[] = [];
  const others: SupplierPick[] = [];
  for (const pick of picks) {
    // Truthiness, not `!== null`: a release-1 server's row has neither field.
    if (pick.lastUsedByMe) mine.push(pick);
    else if ((pick.uses ?? 0) >= MOST_USED_MIN) mostUsed.push(pick);
    else others.push(pick);
  }
  // ISO dates sort as text; a stable sort keeps the server's order among equal dates.
  mine.sort((a, b) => (b.lastUsedByMe ?? '').localeCompare(a.lastUsedByMe ?? ''));
  return { mine, mostUsed, others };
}

/** Letters and digits only, upper-case: how the catalog compares VAT numbers. */
export function normalizeVat(value: string): string {
  return value.replace(/[^0-9a-z]/gi, '').toUpperCase();
}

/**
 * The pick whose VAT number is the document's, if the search found it: «IT01234567890» and
 * «01234567890» are the same Italian number (contract: an «IT» prefix is ignored).
 */
export function pickByVat(picks: readonly SupplierPick[], vat: string): SupplierPick | null {
  const wanted = normalizeVat(vat);
  if (wanted === '') return null;
  return (
    picks.find((pick) => {
      if (!pick.vat) return false;
      const own = normalizeVat(pick.vat);
      const withCountry = normalizeVat(`${pick.vatCountry ?? ''}${pick.vat}`);
      return own === wanted || withCountry === wanted;
    }) ?? null
  );
}
